import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { Button, ExportActionsTypes, FileTypeSelectionCommonForm, Modal, Table } from '@components/common';

import { Icons } from '@icons/icons';

import useConsistencyCheckHistory from '@hooks/cmModule/consistencyCheckHistory/useConsistencyCheckHistory';
import useConsistencyCheckHistoryConsistencyStatuses from '@hooks/cmModule/consistencyCheckHistory/useConsistencyCheckHistoryConsistencyStatuses';
import useConsistencyCheckHistoryInfo from '@hooks/cmModule/consistencyCheckHistory/useConsistencyCheckHistoryInfo';
import useConsistencyCheckHistoryTemplates from '@hooks/cmModule/consistencyCheckHistory/useConsistencyCheckHistoryTemplates';
import useConsistencyCheckHistoryTriggerSources from '@hooks/cmModule/consistencyCheckHistory/useConsistencyCheckHistoryTriggerSources';
import useConsistencyCheckHistoryUsers from '@hooks/cmModule/consistencyCheckHistory/useConsistencyCheckHistoryUsers';
import { useMutateConsistencyHistoryExport } from '@hooks/cmModule/consistencyCheckHistory/useMutateConsistencyHistoryExport';

import { endOfDayInMilliseconds, formatDate, startOfDayInMilliseconds } from '@utils/date';

import { IConsistencyCheckHistory } from '@interfaces/cmModule/consistencyCheck';
import { TConsistencyCheckHistoryExportDTO } from '@interfaces/cmModule/dto/consistencyCheckDTO';
import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ISortBy,
	ITableDatepickerFilter,
	ITablePagination,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';

import { defaultMeta, TExportFileTypes, TMenuActionsTypes } from '@enums/common';

import { exportRadioButtonsData, MIN_FILTER_WIDTH } from '@constants/common';
import { routes } from '@constants/routes';
import { TableFilterTypes } from '@constants/table';
import { exportActions } from '../../../AdminPanel/Alarms/AlarmByVendor/constants';

import ConsistencyCheckHistorySummary from './ConsistencyCheckHistorySummary';
import { getColumns } from './utils';

import styles from './ConsistencyCheckHistory.module.scss';

const MODAL_SIZE = '80%';

const { adminPanel: { index }, cmModule: { consistencyCheck } } = routes;

const ConsistencyCheckHistory = () => {
	const { id = '' } = useParams();

	const navigate = useNavigate();

	const [selectedConsistencyCheck, setSelectedConsistencyCheck] = useState<IConsistencyCheckHistory | null>(null);
	const [isConsistencyCheckSummaryModal, setIsConsistencyCheckSummaryModal] = useState(false);
	const [isOpenFileTypeSelectionModal, setIsOpenFileTypeSelectionModal] = useState(false);

	const [page, setPage] = useState(defaultMeta.page);
	const [pageSize, setPageSize] = useState(defaultMeta.pageSize);
	const [sortDirection, setSortDirection] = useState(false);
	const [sortFieldName, setSortFieldName] = useState<string | null>(null);

	const [referenceTemplate, setReferenceTemplate] = useState<TSelectOption<string> | null>(null);
	const [consistencyStatus, setConsistencyStatus] = useState<TSelectOption<string>>(null);
	const [triggerSource, setTriggerSource] = useState<TSelectOption<string>>(null);
	const [consistencyUser, setConsistencyUser] = useState<TSelectOption<string>[] | null>(null);

	const [createdAtFrom, setCreatedAtFrom] = useState<number | null>(null);
	const [createdAtTo, setCreatedAtTo] = useState<number | null>(null);

	const onBackClickHandler = useCallback(() => navigate(`${index}${consistencyCheck}`), [navigate]);

	const openConsistencySummary = useCallback((consistencyCheck: IConsistencyCheckHistory) => {
		setSelectedConsistencyCheck(consistencyCheck);
		setIsConsistencyCheckSummaryModal(true);
	}, []);

	const closeConsistencySummary = useCallback(() => {
		setSelectedConsistencyCheck(null);
		setIsConsistencyCheckSummaryModal(false);
	}, []);

	const toggleFileTypeSelectionModal = () => setIsOpenFileTypeSelectionModal(prevState => !prevState);
	const closeTypeSelectionForm = () => setIsOpenFileTypeSelectionModal(false);

	const {
		consistencyCheckHistory,
		countOfRecords,
		isLoadingConsistencyCheckHistory,
	} = useConsistencyCheckHistory({
		consistencyId: id,
		page,
		pageSize,
		sortDirection: sortFieldName ? sortDirection : undefined,
		sortFieldName: sortFieldName ? sortFieldName : undefined,
		consistencyStatus: consistencyStatus?.value || undefined,
		goldenTemplateId: referenceTemplate?.value || undefined,
		triggerSource: triggerSource?.value || undefined,
		createdAtFromText: createdAtFrom ? formatDate(startOfDayInMilliseconds(createdAtFrom)) : undefined,
		createdAtToText: createdAtTo ? formatDate(endOfDayInMilliseconds(createdAtTo)) : undefined,
		userIds: consistencyUser?.map(user => user?.value || ''),
	});

	const {
		consistencyCheckInfo,
		isLoadingConsistencyCheckHistoryInfo,
	} = useConsistencyCheckHistoryInfo({ id });

	const {
		consistencyCheckUsers,
		isLoadingConsistencyCheckHistoryUsers,
	} = useConsistencyCheckHistoryUsers({ id });

	const {
		consistencyCheckTemplates,
		isLoadingConsistencyCheckHistoryTemplates,
	} = useConsistencyCheckHistoryTemplates({ id });

	const {
		consistencyCheckConsistencyStatuses,
		isLoadingConsistencyCheckConsistencyStatuses,
	} = useConsistencyCheckHistoryConsistencyStatuses();

	const {
		consistencyCheckTriggerSources,
		isLoadingConsistencyCheckTriggerSources,
	} = useConsistencyCheckHistoryTriggerSources();

	const {
		exportConsistencyHistory,
		isLoadingExportConsistencyHistory,
	} = useMutateConsistencyHistoryExport();

	const [checkedIds, setCheckedIds] = useState<Array<string>>([]);
	const [actionId, setActionId] = useState<TMenuActionsTypes>();

	const handleExport = useCallback((ids: string[], actionType: TMenuActionsTypes) => {
		toggleFileTypeSelectionModal();
		setCheckedIds(ids.filter(Boolean));
		setActionId(actionType);
	}, []);

	const columns = useMemo(() => {
		return getColumns({
			openConsistencySummary,
		});
	}, [openConsistencySummary]);

	const pageHandler = useCallback((page: number) => setPage(page), []);
	const pageSizeHandler = useCallback((pageSize: number) => {
		setPageSize(pageSize);
		setPage(defaultMeta.page);
	}, []);

	const pagination: ITablePagination = useMemo(() => ({
		countOfRecords: countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	}), [
		page,
		pageSize,
		countOfRecords,
		pageHandler,
		pageSizeHandler,
	]);

	const handleConsistencyStatusChange = useCallback((option: TSelectOption<string>) => {
		setConsistencyStatus(option);
		setPage(defaultMeta.page);
	}, []);

	const handleTriggerSourceChange = useCallback((option: TSelectOption<string>) => {
		setTriggerSource(option);
		setPage(defaultMeta.page);
	}, []);

	const handleConsistencyUserChange = useCallback((option: TSelectOption<string>[] | null) => {
		setConsistencyUser(option);
		setPage(defaultMeta.page);
	}, []);

	const handleReferenceTemplateChange = useCallback((option: TSelectOption<string>) => {
		setReferenceTemplate(option);
		setPage(defaultMeta.page);
	}, []);

	const onChangeCreatedAtFrom = useCallback((value: number | null) => {
		setCreatedAtFrom(value);
	}, []);

	const onChangeCreatedAtTo = useCallback((value: number | null) => {
		setCreatedAtTo(value);
	}, []);

	const handleExportConsistencyCheck = (data: { fileType: TExportFileTypes }) => {
		const isAll = actionId === ExportActionsTypes.all || actionId === ExportActionsTypes.allFiltered;
		const exportData: TConsistencyCheckHistoryExportDTO = {
			isAll: isAll,
			ids: !isAll ? checkedIds : [],
			fileType: data.fileType,
			filter: {
				consistencyId: id,
				consistencyStatus: consistencyStatus?.value || undefined,
				createdAtFromText: createdAtFrom ? formatDate(startOfDayInMilliseconds(createdAtFrom)) : undefined,
				createdAtToText: createdAtTo ? formatDate(endOfDayInMilliseconds(createdAtTo)) : undefined,
				goldenTemplateId: referenceTemplate?.value || undefined,
				sortDirection: sortDirection,
				sortFieldName: sortFieldName || undefined,
				triggerSource: triggerSource?.value || undefined,
			},
		};

		exportConsistencyHistory(exportData);
	};

	const dateFilters: ITableDatepickerFilter[] = useMemo(() => [
		{
			name: 'from',
			label: 'Last Checked From',
			handleChange: onChangeCreatedAtFrom,
			selected: createdAtFrom ? new Date(createdAtFrom) : null,
			disableFutureDates: true,
		},
		{
			name: 'to',
			label: 'to',
			handleChange: onChangeCreatedAtTo,
			selected: createdAtTo ? new Date(createdAtTo) : null,
			disableFutureDates: true,
		},
	], [
		createdAtFrom,
		createdAtTo,
		onChangeCreatedAtFrom,
		onChangeCreatedAtTo,
	]);

	const filters: TTableFilter[] = useMemo(() => {
		return [
			{
				name: 'goldenTemplateId',
				placeholder: 'Reference template',
				option: referenceTemplate,
				options: consistencyCheckTemplates,
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.SELECT,
				setState: handleReferenceTemplateChange,
				defaultValue: null,
			},
			{
				name: 'consistencyStatus',
				placeholder: 'Consistency status',
				option: consistencyStatus,
				options: consistencyCheckConsistencyStatuses,
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.SELECT,
				setState: handleConsistencyStatusChange,
				defaultValue: null,
			},
			{
				name: 'triggerSource',
				placeholder: 'Trigger source',
				option: triggerSource,
				options: consistencyCheckTriggerSources,
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.SELECT,
				setState: handleTriggerSourceChange,
				defaultValue: null,
			},
			{
				name: 'consistencyUser',
				placeholder: 'Initiating user',
				option: consistencyUser,
				options: consistencyCheckUsers,
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.MULTISELECT,
				setState: handleConsistencyUserChange,
				defaultValue: null,
			},
		];
	}, [
		consistencyCheckConsistencyStatuses,
		consistencyStatus,
		consistencyCheckTriggerSources,
		triggerSource,
		consistencyCheckUsers,
		consistencyUser,
		consistencyCheckTemplates,
		referenceTemplate,
		handleReferenceTemplateChange,
		handleConsistencyUserChange,
		handleConsistencyStatusChange,
		handleTriggerSourceChange,
	]);

	const handleSort = useCallback((sortBy: ISortBy[]) => {
		setSortDirection(!!sortBy[0]?.desc);
		setSortFieldName(sortBy[0]?.id);
		setPage(defaultMeta.page);
	}, []);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	const isLoading = isLoadingConsistencyCheckHistory
		|| isLoadingConsistencyCheckHistoryInfo
		|| isLoadingConsistencyCheckHistoryUsers
		|| isLoadingConsistencyCheckConsistencyStatuses
		|| isLoadingConsistencyCheckTriggerSources
		|| isLoadingConsistencyCheckHistoryTemplates;

	return (
		<div className={styles.wrapper}>
			<Modal
				height={MODAL_SIZE}
				isOpen={isConsistencyCheckSummaryModal}
				onClose={closeConsistencySummary}
				title='Configuration change summary'
				width={MODAL_SIZE}
			>
				<ConsistencyCheckHistorySummary
					dumpConsistencyId={selectedConsistencyCheck?.id}
				/>
			</Modal>
			<Modal
				isOpen={isOpenFileTypeSelectionModal}
				onClose={closeTypeSelectionForm}
				title='Export'
			>
				<FileTypeSelectionCommonForm
					activeExportAction={ExportActionsTypes.all}
					closeModals={closeTypeSelectionForm}
					exportRadioButtonsData={exportRadioButtonsData}
					handlePreparingExport={handleExportConsistencyCheck}
					isLoading={isLoadingExportConsistencyHistory}
					toggleModal={toggleFileTypeSelectionModal}
				/>
			</Modal>
			<Button
				icon={Icons.arrowLeft()}
				onClick={onBackClickHandler}
				text='Back'
				withoutBackground
			/>
			<div className={styles.header}>
				<div className={styles.title}>{consistencyCheckInfo?.name}</div>
				<div className={styles.detail}>
					<div className={styles.name}>NE type:</div>
					<div className={styles.value}>{consistencyCheckInfo?.configType?.title}</div>
				</div>
			</div>
			<div className={styles.container}>
				<Table
					columns={columns}
					customExportActions={exportActions}
					data={consistencyCheckHistory}
					dateFilters={dateFilters}
					filters={filters}
					handleExport={handleExport}
					isDeleteActionDisabled
					isExport
					isLoading={isLoading}
					isWithoutActions
					isWithoutCheckbox
					isWithoutDeleteAction
					pagination={pagination}
					sort={sort}
				/>
			</div>
		</div>
	);
};

export default ConsistencyCheckHistory;
